<script setup>
// ========================================
// Imports
// ========================================
import { useDisplay } from 'vuetify'
import { refDebounced } from '@vueuse/core'
import SalonerLogo from '@images/logo.svg?skipsvgo' // SVGO strips the viewBox, so a resized logo gets cropped
import TabHeader from '@/components/TabHeader.vue'
import DatePicker from '@/components/DatePicker.vue'
import EmployeeAvatar from '@/components/EmployeeAvatar.vue'
import TablePaginationComplete from '@/components/pagination/TablePaginationComplete.vue'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useModalStore } from '@/stores/ModalStore'
import { extractDate, extractDateFromString, extractTimeFromDateString } from '@/utils/datetime'
import { formatPhone } from '@/utils/strings'
import { SMS_STATUS_TONES } from '@/utils/sms'

// Keeps "Messages" highlighted in the menu
definePage({ meta: { navActiveLink: 'messages' } })

// ========================================
// Composables & Stores
// ========================================
const { t, te } = useI18n()
const { mobile, mdAndDown, xlAndUp } = useDisplay()
const calendarStore = useCalendarStore()
const customerStore = useCustomerStore()
const modalStore = useModalStore()

// ========================================
// State
// ========================================
const tab = ref('outgoing') // outgoing | incoming - also the endpoint name
const phone = ref('')
const state = ref(null) // outgoing only: pending | sent | delivered | failed
const dateFrom = ref(null)
const dateTo = ref(null)
const page = ref(1)
const perPage = ref(20)
const messages = ref([])
const meta = ref({})
const isLoading = ref(true)
const expanded = ref([])

const isOutgoing = computed(() => tab.value === 'outgoing')

// ========================================
// Filters
// ========================================
// BE matches the number as a prefix and refuses fewer than 6 digits - shorter input is not sent
const PHONE_MIN_DIGITS = 6
const countDigits = value => (value || '').replace(/\D/g, '').length

const debouncedPhone = refDebounced(phone, 400)
const phoneQuery = computed(() => countDigits(debouncedPhone.value) >= PHONE_MIN_DIGITS ? debouncedPhone.value.trim() : '')
const phoneHint = computed(() => {
  const digits = countDigits(phone.value)

  return digits && digits < PHONE_MIN_DIGITS ? t('messages_page.history.phone_min_digits') : ''
})

// State groups of BE (SMS_LIST_STATES) - not the 7 statuses, a single gateway code is too fine-grained for a dropdown
const stateOptions = computed(() => ['pending', 'sent', 'delivered', 'failed'].map(value => ({
  title: t(`messages_page.history.state.${value}`),
  value,
})))

// Every filter but the page - any change starts again from page 1
const filterQuery = computed(() => {
  const query = { perPage: perPage.value }
  if (phoneQuery.value) query.phone = phoneQuery.value
  if (isOutgoing.value && state.value) query.state = state.value
  if (dateFrom.value) query.from = extractDate(new Date(dateFrom.value), '-')
  if (dateTo.value) query.to = extractDate(new Date(dateTo.value), '-')

  return query
})

const hasActiveFilters = computed(() => !!(phone.value || (isOutgoing.value && state.value) || dateFrom.value || dateTo.value))

const clearFilters = () => {
  phone.value = ''
  state.value = null
  dateFrom.value = null
  dateTo.value = null
}

// ========================================
// Messages
// ========================================
let lastRequestId = 0

const fetchMessages = async () => {
  const requestId = ++lastRequestId
  isLoading.value = true

  try {
    const response = await $backend(`/communication/${tab.value}`, {
      method: 'GET',
      query: { ...filterQuery.value, page: page.value },
    })
    if (requestId !== lastRequestId || !response?.success) return

    messages.value = response.data?.messages || []
    meta.value = response.meta || {}
    expanded.value = []
  } catch (e) {
    // aborted or failed - $backend already showed the error
  } finally {
    if (requestId === lastRequestId) isLoading.value = false
  }
}

// The other tab's rows don't fit the new columns
watch(tab, () => { messages.value = [] })

watch([tab, filterQuery], () => {
  if (page.value === 1) fetchMessages()
  else page.value = 1 // the page watcher fetches
})

watch(page, fetchMessages)

// ========================================
// Table
// ========================================
const headers = computed(() => [
  { title: t('messages_page.history.client'), key: 'client', sortable: false },
  ...(isOutgoing.value
    ? [
      { title: t('status'), key: 'status', sortable: false },
      { title: t('messages_page.history.sender'), key: 'sender', sortable: false },
    ]
    : []),
  { title: t('messages_page.history.content'), key: 'message', sortable: false, cellProps: { class: { 'history-message-cell': !mobile.value } } },
  { title: t('messages_page.history.date_time'), key: 'dateTime', sortable: false },
])

// sender.userId matches employeeId of calendar employees and deleted employees
const employeesById = computed(() => Object.fromEntries(
  [...calendarStore.availableCalendars, ...(calendarStore.unavailableEmployees || [])]
    .filter(employee => employee.employeeId)
    .map(employee => [String(employee.employeeId), employee]),
))

// "2026-09-24 10:41:25" -> "24.09.2026 10:41"
const formatDate = dateTime => extractDateFromString(dateTime).split('-').reverse().join('.')
const formatDateTime = dateTime => `${formatDate(dateTime)} ${extractTimeFromDateString(dateTime)}`

const rows = computed(() => messages.value.map(message => {
  // Queued messages have no conversationTime yet - only when they are due
  const dateTime = message.plannedTime || message.conversationTime || ''
  const senderType = message.sender?.type === 'employee' ? 'employee' : 'system'
  const status = message.status
  const tone = SMS_STATUS_TONES[status?.tone]

  return {
    ...message,
    date: dateTime && formatDate(dateTime),
    time: dateTime && extractTimeFromDateString(dateTime),
    isPlanned: !!message.plannedTime,
    senderType,
    employee: senderType === 'employee' ? employeesById.value[String(message.sender?.userId)] : null,
    statusView: status && {
      label: te(`messages_page.status.${status.key}`) ? t(`messages_page.status.${status.key}`) : status.label,
      color: tone?.color,
      icon: tone?.icon,
    },
    readAt: message.readTime && formatDateTime(message.readTime),
  }
}))

const openCustomerCard = async salonerClientId => {
  await customerStore.setSelectedCustomer({ salonerClientId })
  await nextTick()
  modalStore.openModal('customerCardDialog')
}

onMounted(() => {
  // Employees for message senders - the calendar page loads them, other pages may not have them yet
  if (!calendarStore.availableCalendars.length) calendarStore.updateCalendarEmployeesAndResources()

  fetchMessages()
})
</script>

<template>
  <TabHeader
    :title="$t('messages_page.history.title')"
    :breadcrumbs="[
      { title: $t('messages'), to: '/messages' },
      { title: $t('history'), to: '/messages/history' },
    ]"
  >
    <span class="ml-auto">
      <VBtn
        variant="outlined"
        color="secondary"
        :size="mobile ? 'small' : 'default'"
        :to="{ name: 'messages' }"
      >
        <VIcon
          icon="ri-arrow-left-line"
          size="18"
          :class="mobile ? '' : 'mr-1'"
        />
        <span v-if="!mobile">{{ $t('back') }}</span>
      </VBtn>
      <VTooltip
        v-if="!mobile"
        activator="parent"
      >
        {{ $t('back') }}
      </VTooltip>
    </span>
  </TabHeader>

  <VContainer
    class="mb-4"
    :class="{
      'px-0': mdAndDown,
      'px-15': !mdAndDown,
    }"
    fluid
  >
    <VCard
      variant="elevated"
      :class="mobile ? 'mx-n4' : ''"
    >
      <div :class="mobile ? 'pa-3' : 'pa-5'">
        <!-- 👉 Direction tabs -->
        <VTabs
          id="history-tabs"
          v-model="tab"
          color="primary"
        >
          <VTab value="outgoing">
            <VIcon
              start
              icon="ri-arrow-right-up-line"
            />
            {{ $t('messages_page.history.tab_outgoing') }}
          </VTab>
          <VTab value="incoming">
            <VIcon
              start
              icon="ri-arrow-left-down-line"
            />
            {{ $t('messages_page.history.tab_incoming') }}
          </VTab>
        </VTabs>
        <VDivider class="mb-6" />

        <!-- 👉 Filters -->
        <div class="d-flex flex-wrap align-start ga-4 mb-4">
          <VTextField
            v-model="phone"
            :label="$t('messages_page.history.phone')"
            :placeholder="$t('messages_page.history.phone_placeholder')"
            :messages="phoneHint"
            prepend-inner-icon="ri-search-line"
            color="secondary"
            variant="outlined"
            density="compact"
            inputmode="tel"
            maxlength="20"
            clearable
            hide-details="auto"
            class="filter-field"
            :class="mobile ? 'w-100' : 'flex-1-1-0'"
          />
          <VSelect
            v-if="isOutgoing"
            v-model="state"
            :label="$t('status')"
            :items="stateOptions"
            color="secondary"
            variant="outlined"
            density="compact"
            clearable
            hide-details
            class="filter-field"
            :class="mobile ? 'w-100' : 'flex-1-1-0'"
          />
          <DatePicker
            v-model="dateFrom"
            :label-text="$t('messages_page.history.date_from')"
            :max-date="dateTo"
            clearable
            teleport
            class="filter-field"
            :class="mobile ? 'w-100' : 'flex-1-1-0'"
          />
          <DatePicker
            v-model="dateTo"
            :label-text="$t('messages_page.history.date_to')"
            :min-date="dateFrom"
            clearable
            teleport
            class="filter-field"
            :class="mobile ? 'w-100' : 'flex-1-1-0'"
          />
          <VBtn
            variant="outlined"
            :disabled="!hasActiveFilters"
            :color="hasActiveFilters ? 'primary' : 'gray'"
            :block="mobile"
            class="px-2 ml-auto flex-0-0-auto"
            style="min-height: 40px;"
            @click="clearFilters"
          >
            <template #prepend>
              <VIcon
                size="18"
                icon="ri-filter-off-line"
                :class="mobile || xlAndUp ? '' : 'mr-n2'"
              />
            </template>
            <span v-if="mobile || xlAndUp">{{ $t('clear_filters') }}</span>
          </VBtn>
        </div>

        <!-- 👉 Messages -->
        <VDataTableServer
          v-model:expanded="expanded"
          class="text-high-emphasis"
          :headers="headers"
          :items="rows"
          :items-length="meta.total || 0"
          :items-per-page="perPage"
          :loading="isLoading"
          :mobile="mobile"
          :hide-default-header="mobile"
          :hover="!mobile"
          :no-data-text="$t('no_matching_results')"
          item-value="id"
          :show-expand="!isOutgoing"
          :expand-on-click="!isOutgoing"
          expand-icon="ri-arrow-down-s-line"
          collapse-icon="ri-arrow-up-s-line"
          hide-default-footer
        >
          <template #loading>
            <VSkeletonLoader type="table-row@5" />
          </template>

          <template #item.client="{ item }">
            <div class="d-flex flex-column py-2">
              <a
                v-if="item.salonerClientId"
                href="#"
                class="font-weight-medium text-high-emphasis text-no-wrap"
                @click.prevent.stop="openCustomerCard(item.salonerClientId)"
              >
                {{ item.clientName || "" }}
              </a>
              <span
                v-if="item.clientPhone"
                class="text-caption text-medium-emphasis text-no-wrap"
              >
                {{ formatPhone(item.clientPhone) }}
              </span>
            </div>
          </template>

          <template #item.status="{ item }">
            <VChip
              v-if="item.statusView"
              size="small"
              :color="item.statusView.color"
              :prepend-icon="item.statusView.icon"
              class="text-no-wrap"
            >
              {{ item.statusView.label }}
            </VChip>
          </template>

          <template #item.sender="{ item }">
            <div class="d-flex align-center ga-2 text-no-wrap">
              <template v-if="item.senderType === 'employee'">
                <EmployeeAvatar
                  v-if="item.employee"
                  :employee="item.employee"
                  size="mico"
                  :show-tooltip="false"
                />
                {{ item.employee ? item.employee.title || item.employee.fullName : $t('employee') }}
              </template>
              <template v-else>
                <SalonerLogo
                  width="14"
                  height="14"
                />
                Saloner
              </template>
            </div>
          </template>

          <template #item.message="{ item }">
            <div class="text-medium-emphasis text-pre-wrap text-break py-2">
              {{ item.message }}
            </div>
          </template>

          <template #item.dateTime="{ item }">
            <div class="d-flex flex-column text-no-wrap">
              <span
                v-if="item.isPlanned"
                class="text-caption font-weight-medium text-warning"
              >
                {{ $t('messages_page.history.planned_for') }}
              </span>
              <span>{{ item.date }} <span class="text-medium-emphasis ml-1">{{ item.time }}</span></span>
            </div>
          </template>

          <!-- Incoming only - details of the message, its text is already in the row -->
          <template #expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length">
                <div class="d-flex flex-wrap gc-5 gr-1 py-3 text-caption text-medium-emphasis">
                  <span v-if="item.salonerSender">
                    {{ $t('messages_page.history.to_number') }}:
                    <b class="font-weight-medium text-high-emphasis">{{ formatPhone(item.salonerSender) }}</b>
                  </span>
                  <span v-if="item.readAt">
                    {{ $t('messages_page.history.read_in_panel') }}:
                    <b class="font-weight-medium text-high-emphasis">{{ item.readAt }}</b>
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </VDataTableServer>

        <TablePaginationComplete
          v-model="page"
          v-model:per-page="perPage"
          class="mt-4"
          :length="Math.ceil((meta.total || 0) / perPage)"
          :page-size-items="[20, 50, 100]"
          show-results-summary
          :filtered-count="meta.total || 0"
          :total-count="meta.totalAll || 0"
          :what="$t('messages_page.history.messages_count')"
        />
      </div>
    </VCard>
  </VContainer>
</template>

<style scoped lang="scss">
#history-tabs {
  .v-tab.v-tab--selected {
    background-color: rgb(var(--v-theme-primary-alpha));
    border-start-start-radius: 8px;
    border-start-end-radius: 8px;
  }
}

// Filters share the row equally - 160px is the narrowest one may get before wrapping
.filter-field {
  min-inline-size: 160px;
}

// Content takes only what the other columns leave and wraps there, so a long SMS never widens the table
:deep(.history-message-cell) {
  inline-size: 100%;
  max-inline-size: 0;
}
</style>
